<template>
    <div class="aca-wrap">
        <!-- 顶部说明：这一段是「AI 推课」链路的最后闭环，运营必须知道不审 = 首页永远空 -->
        <el-alert
            type="info"
            :closable="false"
            show-icon
            style="margin-bottom:16px"
            title="这里的内容「审核通过」后才会出现在公众号 AI 选课首页"
        >
            <template #default>
                <div class="aca-tip">
                    家长评价通过后进入首页「家长怎么说」；宣传短视频发布后进入品牌页视频位。
                    <b>待审 / 驳回的内容家长端一律看不到</b>，所以不审就等于这两块内容永远是空的。
                    <span v-if="pending.review + pending.video > 0" class="aca-tip__pending">
                        当前待审：评价 {{ pending.review }} 条，视频 {{ pending.video }} 条
                    </span>
                </div>
            </template>
        </el-alert>

        <el-tabs v-model="activeTab" @tab-change="onTabChange">
            <!-- ================= 家长评价 ================= -->
            <el-tab-pane name="review">
                <template #label>
                    <span>家长评价</span>
                    <el-badge v-if="pending.review > 0" :value="pending.review" class="aca-badge" />
                </template>

                <div class="aca-filter">
                    <el-select v-model="reviewQuery.auditState" placeholder="全部状态" clearable class="aca-inPut">
                        <el-option label="待审" :value="0" />
                        <el-option label="已通过" :value="1" />
                        <el-option label="已驳回" :value="2" />
                    </el-select>
                    <el-input
                        v-model="reviewQuery.keyword"
                        placeholder="搜索评价内容 / 学员姓名 / 家长称呼"
                        clearable
                        class="aca-inPut"
                        @keyup.enter="searchReview"
                    />
                    <el-button type="primary" @click="searchReview">搜索</el-button>
                    <el-button @click="resetReview">重置</el-button>
                </div>

                <el-table :data="reviewList" border v-loading="reviewLoading" empty-text="没有符合条件的评价">
                    <el-table-column label="提交时间" prop="createTime" min-width="150" />
                    <el-table-column label="家长称呼" min-width="110">
                        <template #default="scope">
                            {{ scope.row.reviewerName || '匿名家长' }}
                        </template>
                    </el-table-column>
                    <el-table-column label="学员" min-width="90">
                        <template #default="scope">{{ scope.row.studentName || '—' }}</template>
                    </el-table-column>
                    <el-table-column label="评分" width="110">
                        <template #default="scope">
                            <span class="aca-star">{{ starText(scope.row.score) }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="评价内容" min-width="260">
                        <template #default="scope">
                            <div class="aca-content">{{ scope.row.content }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="校区" min-width="120">
                        <template #default="scope">{{ scope.row.areaName || '未指定' }}</template>
                    </el-table-column>
                    <el-table-column label="状态" width="100">
                        <template #default="scope">
                            <el-tag :type="reviewStateTag(scope.row.auditState)" effect="light">
                                {{ reviewStateText(scope.row.auditState) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="驳回原因" min-width="140">
                        <template #default="scope">{{ scope.row.auditRemark || '—' }}</template>
                    </el-table-column>
                    <el-table-column label="操作" width="150" fixed="right">
                        <template #default="scope">
                            <el-button
                                v-if="scope.row.auditState !== 1"
                                type="success"
                                link
                                @click="doReviewAudit(scope.row, 1)"
                            >通过</el-button>
                            <el-button
                                v-if="scope.row.auditState !== 2"
                                type="danger"
                                link
                                @click="openReject('review', scope.row)"
                            >驳回</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="aca-pagination">
                    <el-pagination
                        background
                        layout="total, prev, pager, next"
                        :current-page="reviewQuery.pageIndex"
                        :page-size="reviewQuery.pageSize"
                        :total="reviewTotal"
                        @current-change="onReviewPageChange"
                    />
                </div>
            </el-tab-pane>

            <!-- ================= 宣传短视频 ================= -->
            <el-tab-pane name="video">
                <template #label>
                    <span>宣传短视频</span>
                    <el-badge v-if="pending.video > 0" :value="pending.video" class="aca-badge" />
                </template>

                <div class="aca-filter">
                    <el-select v-model="videoQuery.auditState" placeholder="全部状态" clearable class="aca-inPut">
                        <el-option label="待审" :value="0" />
                        <el-option label="已发布" :value="1" />
                        <el-option label="已驳回" :value="2" />
                        <el-option label="已下线" :value="3" />
                    </el-select>
                    <el-input
                        v-model="videoQuery.keyword"
                        placeholder="搜索视频标题 / 教练姓名"
                        clearable
                        class="aca-inPut"
                        @keyup.enter="searchVideo"
                    />
                    <el-button type="primary" @click="searchVideo">搜索</el-button>
                    <el-button @click="resetVideo">重置</el-button>
                </div>

                <el-table :data="videoList" border v-loading="videoLoading" empty-text="还没有教练上传宣传视频">
                    <el-table-column label="提交时间" prop="createTime" min-width="150" />
                    <el-table-column label="教练" min-width="110">
                        <template #default="scope">{{ scope.row.coachName || ('教练 ' + scope.row.coachId) }}</template>
                    </el-table-column>
                    <el-table-column label="标题" min-width="180">
                        <template #default="scope">{{ scope.row.title || '（未命名）' }}</template>
                    </el-table-column>
                    <el-table-column label="视频" width="110">
                        <template #default="scope">
                            <el-link
                                v-if="scope.row.videoUrl"
                                type="primary"
                                :href="scope.row.videoUrl"
                                target="_blank"
                            >打开预览</el-link>
                            <span v-else>—</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="大小" width="90">
                        <template #default="scope">
                            {{ scope.row.sizeKb ? scope.row.sizeKb + ' KB' : '—' }}
                        </template>
                    </el-table-column>
                    <el-table-column label="状态" width="100">
                        <template #default="scope">
                            <el-tag :type="videoStateTag(scope.row.state)" effect="light">
                                {{ videoStateText(scope.row.state) }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="审核备注" min-width="140">
                        <template #default="scope">{{ scope.row.auditRemark || '—' }}</template>
                    </el-table-column>
                    <el-table-column label="操作" width="190" fixed="right">
                        <template #default="scope">
                            <el-button
                                v-if="scope.row.state !== 1"
                                type="success"
                                link
                                @click="doVideoAudit(scope.row, 1)"
                            >发布</el-button>
                            <el-button
                                v-if="scope.row.state !== 2"
                                type="danger"
                                link
                                @click="openReject('video', scope.row)"
                            >驳回</el-button>
                            <el-button
                                v-if="scope.row.state === 1"
                                type="warning"
                                link
                                @click="doVideoAudit(scope.row, 3)"
                            >下线</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="aca-pagination">
                    <el-pagination
                        background
                        layout="total, prev, pager, next"
                        :current-page="videoQuery.pageIndex"
                        :page-size="videoQuery.pageSize"
                        :total="videoTotal"
                        @current-change="onVideoPageChange"
                    />
                </div>
            </el-tab-pane>
        </el-tabs>

        <!-- 驳回原因弹窗：评价与视频共用 -->
        <el-dialog v-model="rejectVisible" :title="rejectTitle" width="480px">
            <el-input
                v-model="rejectRemark"
                type="textarea"
                :rows="4"
                maxlength="200"
                show-word-limit
                :placeholder="rejectKind === 'review'
                    ? '写清为什么不通过（如「含广告内容」「与课程无关」），仅内部留存'
                    : '写清驳回原因，教练端会看到（如「画面抖动模糊，请重拍」）'"
            />
            <template #footer>
                <el-button @click="rejectVisible = false">取消</el-button>
                <el-button type="danger" :loading="rejectSubmitting" @click="submitReject">确定驳回</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import {
    reviewAuditList,
    reviewAudit,
    videoAuditList,
    videoAudit,
    pendingCount
} from '@/api/aiCourseAudit';

export default {
    name: 'AiCourseReviewAudit',
    data() {
        return {
            activeTab: 'review',
            pending: { review: 0, video: 0 },
            // 家长评价
            reviewQuery: { auditState: null, keyword: '', pageIndex: 1, pageSize: 10 },
            reviewList: [],
            reviewTotal: 0,
            reviewLoading: false,
            // 宣传视频
            videoQuery: { auditState: null, keyword: '', pageIndex: 1, pageSize: 10 },
            videoList: [],
            videoTotal: 0,
            videoLoading: false,
            // 驳回弹窗
            rejectVisible: false,
            rejectKind: 'review',
            rejectRow: null,
            rejectRemark: '',
            rejectSubmitting: false
        };
    },
    computed: {
        rejectTitle() {
            return this.rejectKind === 'review' ? '驳回家长评价' : '驳回宣传短视频';
        }
    },
    methods: {
        /* ---------------- 列表 ---------------- */
        getReviewList() {
            this.reviewLoading = true;
            reviewAuditList(this.reviewQuery).then(res => {
                if (res && res.code === 200) {
                    this.reviewList = (res.data && res.data.records) || [];
                    this.reviewTotal = (res.data && res.data.total) || 0;
                } else {
                    this.reviewList = [];
                    this.reviewTotal = 0;
                    this.$message.error((res && res.message) || '评价列表加载失败');
                }
            }).catch(() => {
                this.$message.error('评价列表加载失败，请稍后重试');
            }).finally(() => {
                this.reviewLoading = false;
            });
        },
        getVideoList() {
            this.videoLoading = true;
            videoAuditList(this.videoQuery).then(res => {
                if (res && res.code === 200) {
                    this.videoList = (res.data && res.data.records) || [];
                    this.videoTotal = (res.data && res.data.total) || 0;
                } else {
                    this.videoList = [];
                    this.videoTotal = 0;
                    this.$message.error((res && res.message) || '视频列表加载失败');
                }
            }).catch(() => {
                this.$message.error('视频列表加载失败，请稍后重试');
            }).finally(() => {
                this.videoLoading = false;
            });
        },
        getPending() {
            pendingCount().then(res => {
                if (res && res.code === 200 && res.data) {
                    this.pending = {
                        review: res.data.review || 0,
                        video: res.data.video || 0
                    };
                }
            }).catch(() => {
                // 红点拿不到不影响主流程，静默即可
            });
        },
        searchReview() {
            this.reviewQuery.pageIndex = 1;
            this.getReviewList();
        },
        resetReview() {
            this.reviewQuery = { auditState: null, keyword: '', pageIndex: 1, pageSize: 10 };
            this.getReviewList();
        },
        searchVideo() {
            this.videoQuery.pageIndex = 1;
            this.getVideoList();
        },
        resetVideo() {
            this.videoQuery = { auditState: null, keyword: '', pageIndex: 1, pageSize: 10 };
            this.getVideoList();
        },
        onReviewPageChange(e) {
            this.reviewQuery.pageIndex = e;
            this.getReviewList();
        },
        onVideoPageChange(e) {
            this.videoQuery.pageIndex = e;
            this.getVideoList();
        },
        onTabChange() {
            // 切页签时按需加载，避免首屏打两个请求
            if (this.activeTab === 'video' && !this.videoList.length && !this.videoLoading) {
                this.getVideoList();
            }
        },

        /* ---------------- 审核动作 ---------------- */
        doReviewAudit(row, state) {
            const tip = state === 1
                ? '通过后这条评价会立刻出现在 AI 选课首页「家长怎么说」里，确认通过？'
                : '确认驳回这条评价？';
            this.$confirm(tip, '审核确认', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                reviewAudit({ id: row.id, auditState: state, auditRemark: row.auditRemark || '' }).then(res => {
                    if (res && res.code === 200) {
                        this.$message.success('已通过');
                        this.getReviewList();
                        this.getPending();
                    } else {
                        this.$message.error((res && res.message) || '审核失败');
                    }
                }).catch(() => {
                    this.$message.error('审核失败，请稍后重试');
                });
            }).catch(() => {});
        },
        doVideoAudit(row, state) {
            const map = {
                1: '发布后这条视频会出现在 AI 选课品牌页视频位（同时只有排在最前的 1 条会展示），确认发布？',
                3: '下线后该视频不再展示，确认下线？'
            };
            this.$confirm(map[state] || '确认操作？', '审核确认', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                videoAudit({ id: row.id, auditState: state, auditRemark: row.auditRemark || '' }).then(res => {
                    if (res && res.code === 200) {
                        this.$message.success(state === 1 ? '已发布' : '已下线');
                        this.getVideoList();
                        this.getPending();
                    } else {
                        this.$message.error((res && res.message) || '操作失败');
                    }
                }).catch(() => {
                    this.$message.error('操作失败，请稍后重试');
                });
            }).catch(() => {});
        },
        openReject(kind, row) {
            this.rejectKind = kind;
            this.rejectRow = row;
            this.rejectRemark = '';
            this.rejectVisible = true;
        },
        submitReject() {
            const remark = (this.rejectRemark || '').trim();
            if (!remark) {
                this.$message.warning('请填写驳回原因');
                return;
            }
            this.rejectSubmitting = true;
            const api = this.rejectKind === 'review' ? reviewAudit : videoAudit;
            api({ id: this.rejectRow.id, auditState: 2, auditRemark: remark }).then(res => {
                if (res && res.code === 200) {
                    this.$message.success('已驳回');
                    this.rejectVisible = false;
                    if (this.rejectKind === 'review') {
                        this.getReviewList();
                    } else {
                        this.getVideoList();
                    }
                    this.getPending();
                } else {
                    this.$message.error((res && res.message) || '驳回失败');
                }
            }).catch(() => {
                this.$message.error('驳回失败，请稍后重试');
            }).finally(() => {
                this.rejectSubmitting = false;
            });
        },

        /* ---------------- 展示映射 ---------------- */
        reviewStateText(s) {
            return { 0: '待审', 1: '已通过', 2: '已驳回' }[s] || '未知';
        },
        reviewStateTag(s) {
            return { 0: 'warning', 1: 'success', 2: 'danger' }[s] || 'info';
        },
        videoStateText(s) {
            return { 0: '待审', 1: '已发布', 2: '已驳回', 3: '已下线' }[s] || '未知';
        },
        videoStateTag(s) {
            return { 0: 'warning', 1: 'success', 2: 'danger', 3: 'info' }[s] || 'info';
        },
        starText(score) {
            const n = Number(score) || 0;
            if (n <= 0) return '—';
            return '★'.repeat(Math.min(n, 5));
        }
    },
    created() {
        this.getReviewList();
        this.getPending();
    }
};
</script>

<style scoped>
.aca-wrap {
    padding: 20px 24px;
}
.aca-tip {
    line-height: 1.9;
    font-size: 13px;
}
.aca-tip__pending {
    display: inline-block;
    margin-left: 6px;
    color: #e6a23c;
    font-weight: 600;
}
.aca-filter {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}
.aca-inPut {
    width: 260px;
    margin-right: 10px;
}
.aca-pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
}
.aca-star {
    color: #f7ba2a;
    letter-spacing: 1px;
}
.aca-content {
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.6;
}
.aca-badge {
    margin-left: 6px;
    margin-top: -6px;
}
</style>
